import { ILocationResponse, UpdateIntervalOption } from '@kcftech/prometheus-client';
import { DataSourceTable } from 'components/data_source_table/DataSourceTable';
import { TableColumn } from 'components/data_table/types';
import { DateRelativeToNow } from 'components/date_time/DateRelativeToNow';
import { useUser } from 'contexts/UserContext';
import { LocationsPagedDataSource } from 'custom_hooks/data_hooks/data_source/locationsDataSource';
import { LocationActiveCell } from 'pages/Settings/Locations/table_cells/LocationActiveCell';
import { LocationCorporationCell } from 'pages/Settings/Locations/table_cells/LocationCorporationCell';
import { LocationNameCell } from 'pages/Settings/Locations/table_cells/LocationNameCell';
import { useCallback, useMemo } from 'react';
import { getId } from 'utils/data';

export type LocationsPropTable = Readonly<{
    dataSource: LocationsPagedDataSource;
}>;

export type LocationsTableCellProps = Readonly<{
    rowData: ILocationResponse;
    onChange: () => void;
    className?: string;
}>;

export type LocationsMaxUpdateIntervalProps = Readonly<LocationsTableCellProps> & {
    options: UpdateIntervalOption[];
};

export function LocationsTable({ dataSource }: LocationsPropTable) {
    const user = useUser();
    const { reloadPage } = dataSource;

    const handleActiveChange = useCallback(() => {
        reloadPage();
        user.updateFavorites();
    }, [reloadPage, user]);

    const columns = useMemo<TableColumn<ILocationResponse>[]>(
        () => [
            {
                id: 'Name',
                header: () => 'Location Name',
                value: ({ rowData }) => (
                    <LocationNameCell rowData={rowData} onChange={reloadPage} />
                ),
                primary: true,
                sortOn: 'Name',
            },
            {
                id: 'Corporation',
                header: () => 'Corporation',
                value: ({ rowData }) => (
                    <LocationCorporationCell rowData={rowData} onChange={reloadPage} />
                ),
                sortOn: 'CorporationName',
            },
            {
                id: 'Created',
                header: () => 'Created',
                value: ({ rowData }) => <DateRelativeToNow date={rowData.Created} />,
            },
            {
                id: 'Active',
                header: () => 'Active',
                value: ({ rowData }) => (
                    <LocationActiveCell rowData={rowData} onChange={handleActiveChange} />
                ),
            },
        ],
        [reloadPage, handleActiveChange],
    );

    return (
        <DataSourceTable
            dataSource={dataSource}
            rowKey={getId}
            columns={columns}
            noResultMessage="No locations found."
        />
    );
}
